'use client';

import clsx from 'clsx';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePostHog } from 'posthog-js/react';
import { HomeProductCard, ProductFeature, ProductTabId } from '@/constants/homeProductTabs';
import { BOOK_DEMO_HREF } from '@/constants/publicConversionPaths';
import { getImageUrl } from '@/lib/cdn';

const BLUE = '#0848E7';
const MINT = '#3EA67E';
const MINT_BG = '#E8F8F2';

interface ProductDetailModalProps {
  card: HomeProductCard | null;
  tabId: ProductTabId | null;
  demoVideoUrl?: string;
  startInVideoMode?: boolean;
  onClose: () => void;
}

const CheckIcon: FC = () => (
  <svg
    className='mt-0.5 shrink-0'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke={MINT}
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <polyline points='20 6 9 17 4 12' />
  </svg>
);

const FeatureIcon: FC<{ icon: ProductFeature['icon'] }> = ({ icon }) => {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: MINT,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  switch (icon) {
    case 'signal':
      return (
        <svg {...common}>
          <path d='M9 18h6' />
          <path d='M10 22h4' />
          <path d='M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z' />
        </svg>
      );
    case 'route':
      return (
        <svg {...common}>
          <circle cx='6' cy='19' r='3' />
          <path d='M9 19h8.5a3.5 3.5 0 0 0 0-7H11a3.5 3.5 0 0 1 0-7H18' />
          <circle cx='18' cy='5' r='3' />
        </svg>
      );
    case 'learn':
      return (
        <svg {...common}>
          <path d='M9 18h6' />
          <path d='M10 22h4' />
          <path d='M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z' />
          <path d='M17 3l1 2 2 1-2 1-1 2-1-2-2-1 2-1z' />
        </svg>
      );
    case 'chat':
      return (
        <svg {...common}>
          <path d='M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z' />
        </svg>
      );
    case 'target':
      return (
        <svg {...common}>
          <circle cx='12' cy='12' r='10' />
          <circle cx='12' cy='12' r='6' />
          <circle cx='12' cy='12' r='2' />
        </svg>
      );
    case 'rank':
      return (
        <svg {...common}>
          <path d='M3 3v18h18' />
          <path d='M7 16l4-4 4 3 5-6' />
        </svg>
      );
    case 'page':
      return (
        <svg {...common}>
          <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
          <polyline points='14 2 14 8 20 8' />
        </svg>
      );
    case 'enrich':
      return (
        <svg {...common}>
          <path d='M12 3l1.9 5.8H20l-4.8 3.5 1.8 5.7L12 14.8 7 18l1.8-5.7L4 8.8h6.1z' />
        </svg>
      );
    case 'call':
      return (
        <svg {...common}>
          <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.86.3 1.7.54 2.5a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.58-1.06a2 2 0 0 1 2.11-.45c.8.24 1.64.42 2.5.54A2 2 0 0 1 22 16.92z' />
        </svg>
      );
    case 'email':
      return (
        <svg {...common}>
          <path d='M4 4h16v16H4z' />
          <polyline points='22,6 12,13 2,6' />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <polyline points='20 6 9 17 4 12' />
        </svg>
      );
  }
};


const GridIcon: FC = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden>
    <rect x='3' y='3' width='7' height='7' rx='1' />
    <rect x='14' y='3' width='7' height='7' rx='1' />
    <rect x='3' y='14' width='7' height='7' rx='1' />
    <rect x='14' y='14' width='7' height='7' rx='1' />
  </svg>
);

const ProductDetailModal: FC<ProductDetailModalProps> = ({
  card,
  tabId,
  demoVideoUrl,
  startInVideoMode = false,
  onClose,
}) => {
  const posthog = usePostHog();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideoModeRef = useRef(false);
  const [isVideoMode, setIsVideoMode] = useState(
    Boolean(card && demoVideoUrl && startInVideoMode),
  );
  const [isAnimated, setIsAnimated] = useState(false);

  const hasDemoVideo = Boolean(demoVideoUrl);

  isVideoModeRef.current = isVideoMode;

  const exitVideoMode = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setIsVideoMode(false);
  }, []);

  const handleClose = useCallback(() => {
    exitVideoMode();
    onClose();
  }, [exitVideoMode, onClose]);

  const enterVideoMode = useCallback(() => {
    if (!demoVideoUrl || !card) return;
    setIsVideoMode(true);
    posthog?.capture('homepage_product_modal_video_opened', {
      card: card.title,
      tab: tabId,
    });
  }, [card, demoVideoUrl, posthog, tabId]);

  useEffect(() => {
    if (!isVideoMode) return;
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    void video.play().catch(() => {
      /* autoplay blocked — native controls still available */
    });
    overlayRef.current
      ?.querySelector('[data-modal-hero]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [isVideoMode]);

  useEffect(() => {
    setIsVideoMode(Boolean(card && demoVideoUrl && startInVideoMode));
  }, [card, demoVideoUrl, startInVideoMode]);

  useEffect(() => {
    if (!card) {
      setIsAnimated(false);
      return;
    }
    const raf = requestAnimationFrame(() => setIsAnimated(true));
    return () => {
      cancelAnimationFrame(raf);
      setIsAnimated(false);
    };
  }, [card]);

  useEffect(() => {
    if (!card) return undefined;

    document.body.style.overflow = 'hidden';

    const overlay = overlayRef.current;
    const dialog = dialogRef.current;

    overlay?.scrollTo({ top: 0, left: 0 });
    dialog?.focus({ preventScroll: true });

    const raf = requestAnimationFrame(() => {
      overlay?.scrollTo({ top: 0, left: 0 });
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isVideoModeRef.current) {
        exitVideoMode();
      } else {
        handleClose();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [card, exitVideoMode, handleClose]);

  if (!card) return null;
  const { modal } = card;
  const heroImageSrc = modal.modalImage ?? card.image;
  const heroImageMobileSrc = modal.modalImageMobile ?? card.mobileImage;
  return (
    <div
      ref={overlayRef}
      className='fixed inset-0 z-[100000] overflow-y-auto overscroll-contain'
      role='presentation'
    >
      <div className='pointer-events-none fixed inset-0' aria-hidden>
        <div className='absolute inset-0 bg-slate-900/35' />
        <div className='absolute inset-0 backdrop-blur-[18px] backdrop-saturate-[1.35]' />
        <div className='absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-slate-900/10' />
      </div>

      <div
        className='relative flex min-h-full justify-center sm:px-8 sm:pb-20 sm:pt-24 md:pt-28 lg:px-10 lg:pb-24 lg:pt-32 pt-12'
        onClick={handleClose}
      >
        <div
          ref={dialogRef}
          role='dialog'
          aria-modal
          aria-label={card.title}
          tabIndex={-1}
          className='relative w-full max-w-[1080px] rounded-t-[20px] sm:rounded-[20px] bg-white shadow-[0_32px_64px_-16px_rgba(15,23,42,0.28),0_0_0_1px_rgba(15,23,42,0.04)] outline-none'
          style={{
            transition: 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1), opacity 280ms ease',
            transform: isAnimated ? 'translateY(0) scale(1)' : 'translateY(52px) scale(0.98)',
            opacity: isAnimated ? 1 : 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile drawer pull indicator */}
          <div className='sm:hidden flex justify-center pt-3 pb-1' aria-hidden>
            <div className='h-1 w-10 rounded-full bg-slate-300' />
          </div>
          <button
            type='button'
            onClick={handleClose}
            aria-label='Close'
            className='absolute right-5 top-5 z-20 flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-[#F4F7FF] text-slate-600 transition-colors hover:border-slate-300 hover:text-black sm:right-6 sm:top-6'
          >
            <svg
              width='14'
              height='14'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              aria-hidden
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>

          <div className='px-6 pb-10 pt-14 sm:px-10 sm:pb-12 sm:pt-16 lg:px-14'>
            <div className='grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(260px,320px)] lg:gap-16'>
              <div className='max-w-[640px]'>
                <h2
                  className='mb-4 font-semibold tracking-tight text-black'
                  style={{
                    fontSize: 'clamp(1.5rem, 2.8vw, 2.25rem)',
                    lineHeight: 1.15,
                  }}
                >
                  {modal.modalTitle ?? card.title}
                </h2>
                <p className='mb-8 text-base leading-relaxed text-slate-600 sm:text-[17px] sm:leading-7'>
                  {modal.description}
                </p>
                <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
                  <Link
                    href={modal.ctaHref}
                    className='inline-flex h-11 w-full items-center justify-center rounded-lg px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 sm:w-auto sm:justify-start'
                    style={{ backgroundColor: BLUE }}
                    onClick={handleClose}
                  >
                    Explore playbook
                  </Link>
                  <div className={clsx('grid gap-3 sm:contents', hasDemoVideo ? 'grid-cols-2' : 'grid-cols-1')}>
                    <Link
                      href='/book-demo'
                      className='inline-flex h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 text-sm font-medium text-black transition-colors hover:border-slate-400 sm:justify-start'
                      onClick={handleClose}
                    >
                      Book a demo
                    </Link>
                    {hasDemoVideo && (
                      <button
                        type='button'
                        onClick={isVideoMode ? exitVideoMode : enterVideoMode}
                        aria-pressed={isVideoMode}
                        className={clsx(
                          'inline-flex h-11 items-center justify-center gap-2 rounded-lg border px-5 text-sm font-medium transition-all duration-200 sm:justify-start',
                          isVideoMode
                            ? 'border-[#0848E7] bg-[#F2F5FF] text-[#0848E7] shadow-[0_0_0_1px_rgba(8,72,231,0.15)]'
                            : 'border-slate-300 bg-white text-black hover:border-slate-400',
                        )}
                      >
                        {isVideoMode ? (
                          <span
                            className='inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0848E7] text-white'
                            aria-hidden
                          >
                            <GridIcon />
                          </span>
                        ) : (
                          <Image src={getImageUrl('icons/play.svg')} alt='' width={20} height={20} aria-hidden />
                        )}
                        {isVideoMode ? 'Back to overview' : 'Watch demo'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <ul className='space-y-3.5 lg:pt-2'>
                {modal.bullets.map((b) => (
                  <li key={b} className='flex items-start gap-2.5 text-[15px] leading-snug text-slate-700'>
                    <CheckIcon />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Hero media — swap screenshot for inline demo video (no stacked opacity layers) */}
            <div
              data-modal-hero
              className={clsx(
                'relative mt-10 overflow-hidden rounded-2xl border shadow-sm transition-colors duration-300 sm:mt-12',
                isVideoMode
                  ? 'border-slate-900/10 bg-black'
                  : 'border-slate-200 bg-[#F4F7FB]',
              )}
            >
              {isVideoMode && hasDemoVideo ? (
                <div className='flex flex-col'>
                  <div className='relative aspect-video w-full bg-black'>
                    <video
                      ref={videoRef}
                      src={demoVideoUrl}
                      controls
                      controlsList='nodownload'
                      playsInline
                      preload='metadata'
                      className='lp-modal-video h-full w-full'
                      style={{ backgroundColor: '#000' }}
                      aria-label={`${tabId === 'sales' ? 'Sales' : 'Marketing'} product demo for ${card.title}`}
                    />
                  </div>
                  <div className='flex items-center justify-between gap-3 border-t border-white/10 bg-slate-950 px-4 py-3 text-white'>
                    <p className='text-sm font-medium'>
                      {tabId === 'sales' ? 'Sales' : 'Marketing'} demo
                    </p>
                    <button
                      type='button'
                      onClick={exitVideoMode}
                      className='inline-flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-1.5 text-xs font-medium text-white/90 transition-colors hover:border-white/40 hover:text-white'
                    >
                      <GridIcon />
                      Back to overview
                    </button>
                  </div>
                </div>
              ) : (
                <div className='relative'>
                  {heroImageMobileSrc && (
                    <Image
                      src={heroImageMobileSrc}
                      alt={card.imageAlt}
                      width={720}
                      height={960}
                      className='block h-auto w-full sm:hidden'
                    />
                  )}
                  <Image
                    src={heroImageSrc}
                    alt={card.imageAlt}
                    width={1080}
                    height={620}
                    className={clsx(
                      heroImageMobileSrc ? 'hidden sm:block' : 'block',
                      'h-auto w-full',
                    )}
                  />
                  <div className='pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#F4F7FB] to-transparent' />
                </div>
              )}
            </div>

            <div className='mt-8 grid grid-cols-1 gap-8 sm:mt-10 md:grid-cols-3 md:gap-6 lg:gap-8'>
              {modal.features.map((f) => (
                <div key={f.title} className='max-w-[320px] md:max-w-none'>
                  <div
                    className='mb-4 flex h-8 w-8 items-center justify-center rounded-full'
                    style={{ backgroundColor: MINT_BG }}
                  >
                    <FeatureIcon icon={f.icon} />
                  </div>
                  <h3 className='mb-2 text-[15px] font-semibold leading-snug text-black sm:text-base'>
                    {f.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-slate-500'>{f.description}</p>
                </div>
              ))}
            </div>

            <div
              className={clsx(
                'mt-14 overflow-hidden transition-all duration-300 ease-out',
                isVideoMode ? 'pointer-events-none max-h-0 opacity-0' : 'max-h-[2000px] opacity-100',
              )}
              aria-hidden={isVideoMode}
            >
              <p className='mb-5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400'>
                More to discover
              </p>
              <div className='grid grid-cols-1 gap-5 sm:grid-cols-3'>
                {modal.discoverCards.map((d) => (
                  <div
                    key={d.title}
                    className='overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md'
                  >
                    <div className='h-[200px] overflow-hidden border-b border-slate-100 bg-[#F4F7FB]'>
                      {d.mobileImage && (
                        <Image
                          src={d.mobileImage}
                          alt={d.title}
                          width={340}
                          height={200}
                          className='block h-full w-full object-contain object-top sm:hidden'
                        />
                      )}
                      <Image
                        src={d.image}
                        alt={d.title}
                        width={340}
                        height={200}
                        className={clsx(
                          d.mobileImage ? 'hidden sm:block' : 'block',
                          'h-full w-full object-contain object-top',
                        )}
                      />
                    </div>
                    <div className='p-4'>
                      <h4 className='mb-1.5 text-sm font-semibold leading-snug text-black'>
                        {d.title}
                      </h4>
                      <p className='text-xs leading-relaxed text-slate-500'>{d.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className='mt-12 rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center sm:px-10'>
              <p className='mb-3 text-xs font-medium tracking-[0.4em] text-slate-400'>
                {modal.testimonial.company}
              </p>
              <svg
                width='28'
                height='22'
                viewBox='0 0 40 32'
                fill={BLUE}
                className='mb-3 opacity-60'
                aria-hidden
              >
                <path d='M0 32V18C0 8 5.6 2 16 0v7c-4.8 1.2-7.2 4-7.2 8H16v17H0zm24 0V18c0-10 5.6-16 16-18v7c-4.8 1.2-7.2 4-7.2 8H40v17H24z' />
              </svg>
              <blockquote
                className='mx-auto mb-4 max-w-[620px] font-medium text-black'
                style={{ fontSize: 'clamp(1rem, 1.5vw, 1.125rem)', lineHeight: 1.55 }}
              >
                &ldquo;{modal.testimonial.quote}&rdquo;
              </blockquote>
              <p className='text-sm text-slate-500'>
                {modal.testimonial.name}, {modal.testimonial.role}, {modal.testimonial.company}
              </p>
            </div>

            <div className='mt-10 flex flex-row items-center justify-between gap-4 sm:border-t sm:border-slate-200 sm:pt-8'>
              <span className='text-[15px] font-medium text-black'>{modal.footerCtaLabel}</span>
              <Link
                href={BOOK_DEMO_HREF}
                className='inline-flex h-10 shrink-0 items-center rounded-md bg-[#F2F5FF] px-5 text-sm font-medium text-[#0848E7] transition-colors hover:bg-[#E5EAFF]'
                onClick={handleClose}
              >
                Book a call
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
